import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALL_API_KEY_SCOPES,
  companyApiKeyService,
  ApiKeyScope,
} from '../services/companyApiKeyService';
import { useTranslation } from '../hooks/useTranslation';

interface Props {
  companyId: string;
}

const CompanyApiKeysSettings: React.FC<Props> = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('Document agent');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([...ALL_API_KEY_SCOPES]);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: keys = [], isLoading } = useQuery({
    queryKey: ['company-api-keys', companyId],
    queryFn: () => companyApiKeyService.list(companyId),
  });

  const createMutation = useMutation({
    mutationFn: () => companyApiKeyService.create(companyId, { name: name.trim(), scopes }),
    onSuccess: (data) => {
      setCreatedKey(data.apiKey || null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['company-api-keys', companyId] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message || e?.message || 'Error');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => companyApiKeyService.revoke(companyId, keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-api-keys', companyId] });
    },
  });

  const activeKeys = useMemo(() => keys.filter((k) => !k.revokedAt), [keys]);
  const revokedKeys = useMemo(() => keys.filter((k) => !!k.revokedAt), [keys]);

  const toggleScope = (scope: ApiKeyScope) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {t('settings.apiKeys.title') || 'API keys (document connector)'}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('settings.apiKeys.hint') ||
            'Ключ даёт доступ к /api/connector/v1 (контрагенты, приходы, контракты). Показывается один раз при создании.'}
        </p>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {t('settings.apiKeys.name') || 'Название'}
          <input
            className="mt-1 w-full border rounded px-3 py-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Document agent"
          />
        </label>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            {t('settings.apiKeys.scopes') || 'Scopes'}
          </div>
          <div className="flex flex-wrap gap-3">
            {ALL_API_KEY_SCOPES.map((scope) => (
              <label key={scope} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopes.includes(scope)}
                  onChange={() => toggleScope(scope)}
                />
                {scope}
              </label>
            ))}
          </div>
        </div>
        <button
          type="button"
          disabled={!name.trim() || scopes.length === 0 || createMutation.isPending}
          onClick={() => createMutation.mutate()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {createMutation.isPending
            ? (t('common.loading') || '...')
            : (t('settings.apiKeys.create') || 'Создать ключ')}
        </button>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {createdKey && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <p className="font-medium text-amber-900 mb-1">
              {t('settings.apiKeys.copyOnce') || 'Скопируйте ключ сейчас — больше он не покажется:'}
            </p>
            <code className="break-all text-amber-950 select-all">{createdKey}</code>
          </div>
        )}
      </div>

      <div>
        <h4 className="font-medium text-gray-900 mb-2">
          {t('settings.apiKeys.active') || 'Активные'}
        </h4>
        {isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading') || '...'}</p>
        ) : activeKeys.length === 0 ? (
          <p className="text-sm text-gray-500">{t('settings.apiKeys.empty') || 'Нет ключей'}</p>
        ) : (
          <ul className="divide-y border rounded">
            {activeKeys.map((k) => (
              <li key={k.id} className="p-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div>
                  <div className="font-medium">{k.name}</div>
                  <div className="text-gray-500">
                    {k.keyPrefix}… · {(k.scopes || []).join(', ')}
                  </div>
                </div>
                <button
                  type="button"
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                  disabled={revokeMutation.isPending}
                  onClick={() =>
                    window.confirm(
                      t('settings.apiKeys.revokeConfirm') || 'Отозвать ключ?'
                    ) && revokeMutation.mutate(k.id)
                  }
                >
                  {t('settings.apiKeys.revoke') || 'Отозвать'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {revokedKeys.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-500 mb-2">
            {t('settings.apiKeys.revoked') || 'Отозванные'}
          </h4>
          <ul className="text-sm text-gray-500 space-y-1">
            {revokedKeys.map((k) => (
              <li key={k.id}>
                {k.name} ({k.keyPrefix}…)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CompanyApiKeysSettings;
